// client/src/components/notifications/NotificationSettings.js
import React, { useState, useEffect } from 'react'
import NotificationPreferencesService from '../../api/notificationPreferencesService'
import ApiAuth from '../../api/ApiAuth'
import ApiUsers from '../../api/ApiUsers'
import toastr from 'toastr'
import PageLayout from '../common/layout/PageLayout'
import './NotificationSettings.css'

// Event type descriptions for UI
const EVENT_DESCRIPTIONS = {
  'proposal.received': {
    title: 'Match Proposal Received',
    description: 'When another player proposes a match with you',
  },
  'proposal.expiring': {
    title: 'Proposal Expiring Soon',
    description: 'Reminder when a match proposal is about to expire',
  },
  'availability.missing': {
    title: 'Availability Missing',
    description: 'Reminder to add your availability for the current period',
  },
  'availability.overlap_found': {
    title: 'Availability Overlap Found',
    description: 'When you and another player have overlapping availability',
  },
}

function NotificationSettings() {
  const [user, setUser] = useState(null)
  const [preferences, setPreferences] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savingPreferences, setSavingPreferences] = useState(new Set())
  const [phoneNumber, setPhoneNumber] = useState('')
  const [editingPhone, setEditingPhone] = useState(false)
  const [showConsentDialog, setShowConsentDialog] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)

      // Load user data
      const userData = await ApiAuth.me()
      setUser(userData)
      setPhoneNumber(userData.phone_e164 || '')

      // Load preferences if user has granted consent
      if (userData.sms_consent_status === 'granted') {
        const prefs = await NotificationPreferencesService.getPreferences()
        setPreferences(prefs)
      }
    } catch (error) {
      console.error('Error loading notification settings:', error)
      toastr.error('Failed to load notification settings')
    } finally {
      setLoading(false)
    }
  }

  const handleTogglePreference = async (eventType, channel, currentValue) => {
    const prefKey = `${eventType}:${channel}`

    // Prevent race conditions - exit if already saving this preference
    if (savingPreferences.has(prefKey)) {
      return
    }

    // Mark this preference as being saved
    setSavingPreferences((prev) => new Set(prev).add(prefKey))

    // Optimistically update UI
    const updatedPrefs = preferences.map((pref) =>
      pref.event_type === eventType && pref.channel === channel
        ? { ...pref, enabled: !currentValue }
        : pref,
    )
    setPreferences(updatedPrefs)

    try {
      // Send update to backend
      const prefsToUpdate = updatedPrefs.map((p) => ({
        event_type: p.event_type,
        channel: p.channel,
        enabled: p.enabled,
      }))
      await NotificationPreferencesService.updatePreferences(prefsToUpdate)
      toastr.success('Preferences updated')
    } catch (error) {
      console.error('Error updating preference:', error)
      toastr.error('Failed to update preference')
      // Revert on error
      await loadData()
    } finally {
      // Remove from saving set
      setSavingPreferences((prev) => {
        const next = new Set(prev)
        next.delete(prefKey)
        return next
      })
    }
  }

  const handleGrantConsent = async () => {
    if (!phoneNumber || phoneNumber.trim() === '') {
      toastr.error('Please enter a phone number first')
      return
    }

    try {
      setSaving(true)
      setShowConsentDialog(false)

      // Update phone and grant consent
      await ApiUsers.updatePhone({
        phone_e164: phoneNumber,
        sms_consent_status: 'granted',
      })

      toastr.success('SMS notifications enabled!')
      await loadData()
    } catch (error) {
      console.error('Error granting consent:', error)
      toastr.error(error.message || 'Failed to enable SMS notifications')
    } finally {
      setSaving(false)
    }
  }

  const handleRevokeConsent = async () => {
    if (
      !window.confirm(
        'Are you sure you want to disable SMS notifications? This will turn off all SMS alerts.',
      )
    ) {
      return
    }

    try {
      setSaving(true)
      await ApiUsers.updatePhone({
        sms_consent_status: 'revoked',
      })
      toastr.success('SMS notifications disabled')
      await loadData()
    } catch (error) {
      console.error('Error revoking consent:', error)
      toastr.error('Failed to disable SMS notifications')
    } finally {
      setSaving(false)
    }
  }

  const handleUpdatePhone = async () => {
    try {
      setSaving(true)
      await ApiUsers.updatePhone({
        phone_e164: phoneNumber || null,
      })
      toastr.success('Phone number updated')
      setEditingPhone(false)
      await loadData()
    } catch (error) {
      console.error('Error updating phone:', error)
      toastr.error(error.message || 'Failed to update phone number')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <PageLayout>
        <div className="container mt-4">
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </PageLayout>
    )
  }

  const hasConsent = user?.sms_consent_status === 'granted'
  const isRevoked = user?.sms_consent_status === 'revoked'

  return (
    <PageLayout>
      <div className="container mt-4 notification-settings-container">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-8">
            <div className="card shadow-sm">
              <div className="card-body p-4">
                <h2 className="mb-4">
                  <i className="bi bi-bell me-2"></i>
                  Notification Settings
                </h2>

                {/* Phone Number Section */}
                <div className="mb-4 pb-4 border-bottom">
                  <h5 className="mb-3">Phone Number</h5>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    {editingPhone ? (
                      <>
                        <label
                          htmlFor="phone-input"
                          className="visually-hidden"
                        >
                          Phone Number
                        </label>
                        <input
                          id="phone-input"
                          type="tel"
                          className="form-control"
                          placeholder="+1 (555) 123-4567"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          disabled={saving}
                          aria-label="Phone number in international format"
                        />
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={handleUpdatePhone}
                          disabled={saving}
                        >
                          Save
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => {
                            setEditingPhone(false)
                            setPhoneNumber(user?.phone_e164 || '')
                          }}
                          disabled={saving}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <label
                          htmlFor="phone-display"
                          className="visually-hidden"
                        >
                          Phone Number
                        </label>
                        <input
                          id="phone-display"
                          type="text"
                          className="form-control"
                          value={phoneNumber || 'Not set'}
                          disabled
                          aria-label="Current phone number"
                        />
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => setEditingPhone(true)}
                        >
                          <i className="bi bi-pencil me-1"></i>
                          Edit
                        </button>
                      </>
                    )}
                  </div>
                  <small className="text-muted">
                    Enter your phone number in international format (e.g.,
                    +15551234567)
                  </small>
                </div>

                {/* SMS Consent Section */}
                <div className="mb-4 pb-4 border-bottom">
                  <h5 className="mb-3">SMS Notifications</h5>

                  {!hasConsent && !isRevoked && (
                    <div className="alert alert-info">
                      <p className="mb-2">
                        <strong>Enable SMS notifications</strong> to receive
                        important updates about match proposals, availability,
                        and more.
                      </p>
                      <p className="mb-2 small">
                        <strong>Note:</strong> Message and data rates may apply.
                        You can opt out at any time.
                      </p>
                      {!phoneNumber && (
                        <p className="mb-2 text-warning">
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          Please add a phone number first
                        </p>
                      )}
                      <button
                        className="btn btn-primary mt-2"
                        onClick={() => setShowConsentDialog(true)}
                        disabled={!phoneNumber || saving}
                      >
                        Enable SMS Notifications
                      </button>
                    </div>
                  )}

                  {isRevoked && (
                    <div className="alert alert-warning">
                      <p className="mb-2">
                        <strong>SMS notifications are disabled.</strong>
                      </p>
                      <p className="mb-0 small">
                        You previously opted out of SMS notifications. Click
                        below to re-enable them.
                      </p>
                      <button
                        className="btn btn-primary mt-2"
                        onClick={() => setShowConsentDialog(true)}
                        disabled={!phoneNumber || saving}
                      >
                        Re-enable SMS Notifications
                      </button>
                    </div>
                  )}

                  {hasConsent && (
                    <div className="alert alert-success">
                      <p className="mb-2">
                        <i className="bi bi-check-circle me-1"></i>
                        <strong>SMS notifications are enabled</strong>
                      </p>
                      <p className="mb-2 small">
                        You will receive text messages at {phoneNumber}
                      </p>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={handleRevokeConsent}
                        disabled={saving}
                      >
                        Disable SMS Notifications
                      </button>
                    </div>
                  )}
                </div>

                {/* Notification Preferences (only show if consent granted) */}
                {hasConsent && preferences.length > 0 && (
                  <div>
                    <h5 className="mb-3">Notification Preferences</h5>
                    <p className="text-muted mb-3">
                      Choose which types of notifications you want to receive
                      via SMS.
                    </p>

                    <div className="list-group">
                      {Object.entries(EVENT_DESCRIPTIONS).map(
                        ([eventType, info]) => {
                          const pref = preferences.find(
                            (p) =>
                              p.event_type === eventType && p.channel === 'sms',
                          )
                          const isEnabled = pref?.enabled || false
                          const prefKey = `${eventType}:sms`
                          const isSaving = savingPreferences.has(prefKey)

                          return (
                            <div
                              key={eventType}
                              className="list-group-item d-flex justify-content-between align-items-center"
                            >
                              <div className="flex-grow-1 me-3">
                                <h6 className="mb-1">{info.title}</h6>
                                <small className="text-muted">
                                  {info.description}
                                </small>
                              </div>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  checked={isEnabled}
                                  onChange={() =>
                                    handleTogglePreference(
                                      eventType,
                                      'sms',
                                      isEnabled,
                                    )
                                  }
                                  disabled={saving || isSaving}
                                  id={`switch-${eventType}`}
                                />
                                <label
                                  className="form-check-label visually-hidden"
                                  htmlFor={`switch-${eventType}`}
                                >
                                  {info.title}
                                </label>
                              </div>
                            </div>
                          )
                        },
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Consent Confirmation Modal */}
      {showConsentDialog && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Enable SMS Notifications</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowConsentDialog(false)}
                  disabled={saving}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  By enabling SMS notifications, you agree to receive text
                  messages at <strong>{phoneNumber}</strong> for:
                </p>
                <ul>
                  <li>Match proposal notifications</li>
                  <li>Availability reminders</li>
                  <li>Important league updates</li>
                </ul>
                <p className="text-muted small mb-0">
                  Message and data rates may apply. You can opt out at any time
                  by disabling SMS notifications in your settings.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowConsentDialog(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleGrantConsent}
                  disabled={saving}
                >
                  {saving ? 'Enabling...' : 'I Agree - Enable SMS'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  )
}

export default NotificationSettings
